<template>
  <div
    class="self-stretch p-3 bg-fill-brand-secondary rounded-xl flex-col justify-end items-end gap-3 flex"
  >
    <TextInput
      v-if="!isNew"
      :model-value="item.id"
      :label="$t('channel.leaderboard.channelActionsEdit.actionId')"
      readonly
    />
    <form.Field name="title">
      <template #default="{ field }">
        <TextInput
          :model-value="field.state.value"
          :label="$t('channel.leaderboard.channelActionsEdit.actionTitle')"
          :placeholder="
            $t('channel.leaderboard.channelActionsEdit.enterActionTitle')
          "
          :error="field.state.meta.errors[0]?.message"
          @update:model-value="(val) => field.handleChange(val)"
        />
      </template>
    </form.Field>
    <form.Field name="shortTitle">
      <template #default="{ field }">
        <TextInput
          :model-value="field.state.value"
          :label="$t('channel.leaderboard.channelActionsEdit.actionShortTitle')"
          :placeholder="
            $t('channel.leaderboard.channelActionsEdit.enterActionShortTitle')
          "
          :error="field.state.meta.errors[0]?.message"
          @update:model-value="(val) => field.handleChange(val)"
        />
      </template>
    </form.Field>
    <form.Field name="notificationTitle">
      <template #default="{ field }">
        <TextInput
          :model-value="field.state.value"
          :label="
            $t('channel.leaderboard.channelActionsEdit.actionNotificationTitle')
          "
          :placeholder="
            $t(
              'channel.leaderboard.channelActionsEdit.enterActionNotificationTitle',
            )
          "
          :error="field.state.meta.errors[0]?.message"
          @update:model-value="(val) => field.handleChange(val)"
        />
      </template>
    </form.Field>

    <!-- App Action Select -->
    <form.Field name="appActionName">
      <template #default="{ field }">
        <div class="self-stretch flex-col justify-start items-start gap-1 flex">
          <div
            class="self-stretch justify-start items-center gap-2 inline-flex"
          >
            <div
              class="grow shrink basis-0 text-white text-sm font-medium font-sans leading-tight"
            >
              {{ $t("channel.leaderboard.channelActionsEdit.baseAppAction") }}
            </div>
            <div
              class="text-white/80 text-xs font-semibold font-sans leading-[18px]"
            >
              {{ $t("channel.leaderboard.channelActionsEdit.required") }}
            </div>
          </div>
          <div
            class="self-stretch h-11 px-4 bg-surface rounded-lg border-2 border-fill-brand justify-start items-center gap-2 inline-flex"
          >
            <select
              :value="field.state.value"
              class="grow shrink basis-0 text-white text-sm font-medium font-sans leading-tight bg-transparent"
              @change="
                (e) => {
                  const value = (e.target as HTMLSelectElement).value;
                  field.handleChange(value);
                }
              "
            >
              <option value="" disabled>
                {{
                  $t("channel.leaderboard.channelActionsEdit.selectAnAppAction")
                }}
              </option>
              <option
                v-for="action in appActionOptions"
                :key="action.name"
                :value="action.name"
              >
                {{ action.title }} ({{ action.points }}
                {{ $t("channel.leaderboard.channelActionsEdit.points") }})
              </option>
            </select>
          </div>
          <div
            v-if="field.state.meta.errors[0]?.message"
            class="text-red-400 text-xs"
          >
            {{ field.state.meta.errors[0]?.message }}
          </div>
        </div>
      </template>
    </form.Field>

    <button
      :disabled="isDeleting"
      class="self-stretch h-10 px-6 py-1 bg-fill-brand-secondary rounded-lg justify-center items-center gap-1 inline-flex hover:bg-fill-brand-secondary-hover transition-colors text-center text-white text-sm font-medium leading-tight disabled:opacity-50 disabled:cursor-not-allowed"
      @click="confirmDeletion"
    >
      {{ $t("channel.leaderboard.channelActionsEdit.delete") }}
    </button>
    <button
      :disabled="isSaving"
      class="self-stretch h-10 px-6 py-1 bg-fill-brand-secondary rounded-lg justify-center items-center gap-1 inline-flex hover:bg-fill-brand-secondary-hover transition-colors text-center text-white text-sm font-medium leading-tight disabled:opacity-50 disabled:cursor-not-allowed"
      @click="form.handleSubmit()"
    >
      {{ $t("channel.leaderboard.channelActionsEdit.save") }}
    </button>
  </div>

  <SubtleConfirmationModal
    v-model="isConfirmingDelete"
    :title="$t('channel.leaderboard.channelActionsEdit.deleteChannelAction')"
    :confirm-label="$t('channel.leaderboard.channelActionsEdit.delete')"
    @confirm="deleteChannelAction"
  >
    <ConfirmationModalBody>
      {{
        $t("channel.leaderboard.channelActionsEdit.deleteActionConfirmation")
      }}
    </ConfirmationModalBody>
  </SubtleConfirmationModal>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { z } from "zod/v4";
import { useForm } from "@tanstack/vue-form";
import { useMutation, useQuery } from "@vue/apollo-composable";
import SubtleConfirmationModal from "@/components/ui/modal/subtle-confirmation-modal.vue";
import { useAwaitAction } from "@/composables/use-await-action";
import { useToastStore } from "@/stores/toast.store";
import { graphql as gql } from "@/generated";
import { prettifyGraphqlError } from "@/utils/format";
import TextInput from "../ui/form/text-input.vue";
import ConfirmationModalBody from "../ui/modal/confirmation-modal/confirmation-modal-body.vue";
import type { LocalChannelAction } from "./channel-actions-edit.vue";
import { useI18n } from "vue-i18n";
const GetAppActionsDocument = gql(/* GraphQL */ `
  query GetAppActionsForChannelActions {
    getAppActions {
      ... on AppActionForChannelAdminList {
        list {
          name
          title
          points
          notification_title
        }
      }
    }
  }
`);

const CreateChannelActionDocument = gql(/* GraphQL */ `
  mutation CreateChannelAction($input: CreateChannelActionInput!) {
    createChannelAction(input: $input) {
      ... on ChannelActionForChannelAdmin {
        id
        title
        short_title
        notification_title
        app_action_name
      }

      ... on NotFoundError {
        error
      }
    }
  }
`);

const UpdateChannelActionDocument = gql(/* GraphQL */ `
  mutation UpdateChannelAction($input: UpdateChannelActionInput!) {
    updateChannelAction(input: $input) {
      ... on ChannelActionForChannelAdmin {
        id
        title
        short_title
        notification_title
        app_action_name
      }

      ... on NotFoundError {
        error
      }
    }
  }
`);

const DeleteChannelActionDocument = gql(/* GraphQL */ `
  mutation DeleteChannelAction($id: String!) {
    deleteChannelAction(id: $id) {
      ... on ChannelActionSuccess {
        success
      }
      ... on NotFoundError {
        error
      }
    }
  }
`);

const { t } = useI18n();
const props = defineProps<{
  item: LocalChannelAction;
  isNew: boolean;
  availableAppActions: string[];
}>();

const emit = defineEmits<{
  deleted: [];
  saved: [];
}>();

const toastStore = useToastStore();

const isConfirmingDelete = ref(false);

const { result: appActionsResult } = useQuery(GetAppActionsDocument);

const appActionOptions = computed(() => {
  const result = appActionsResult.value?.getAppActions;
  return result && "list" in result ? result.list : [];
});

const schema = z.object({
  title: z
    .string()
    .min(1, t("channel.leaderboard.channelActionsEdit.titleRequired")),
  shortTitle: z
    .string()
    .min(1, t("channel.leaderboard.channelActionsEdit.shortTitleRequired")),
  notificationTitle: z
    .string()
    .min(
      1,
      t("channel.leaderboard.channelActionsEdit.notificationTitleRequired"),
    ),
  appActionName: z
    .string()
    .min(1, t("channel.leaderboard.channelActionsEdit.appActionNameRequired")),
});

const { mutate: createMutation } = useMutation(CreateChannelActionDocument, {
  throws: "never",
  errorPolicy: "all",
});
const { mutate: updateMutation } = useMutation(UpdateChannelActionDocument, {
  throws: "never",
  errorPolicy: "all",
});
const { mutate: deleteMutation } = useMutation(DeleteChannelActionDocument, {
  throws: "never",
  errorPolicy: "all",
});

function confirmDeletion() {
  isConfirmingDelete.value = true;
}

const { handler: deleteChannelAction, loading: isDeleting } = useAwaitAction(
  async () => {
    isConfirmingDelete.value = false;
    const response = await deleteMutation({ id: props.item.id! });

    const data = response?.data?.deleteChannelAction;
    if (!data || "error" in data) {
      toastStore.addToast({
        title: t("channel.leaderboard.channelActionsEdit.error"),
        message: `${t("channel.leaderboard.channelActionsEdit.failedToDeleteAction")}: ${
          data && "error" in data
            ? data.error
            : prettifyGraphqlError(response?.errors)
        }`,
        type: "error",
      });
    } else {
      toastStore.addToast({
        title: t("channel.leaderboard.channelActionsEdit.actionDeleted"),
        message: t(
          "channel.leaderboard.channelActionsEdit.actionDeletedSuccessfully",
        ),
        type: "success",
      });
      emit("deleted");
    }
  },
);

const { handler: saveChannelAction, loading: isSaving } = useAwaitAction(
  async (values: {
    title: string;
    shortTitle: string;
    notificationTitle: string;
    appActionName: string;
  }) => {
    const input = {
      title: values.title,
      short_title: values.shortTitle,
      notification_title: values.notificationTitle,
      app_action_name: values.appActionName,
    };

    if (props.isNew) {
      const response = await createMutation({
        input,
      });

      const data = response?.data?.createChannelAction;
      if (!data) {
        toastStore.addToast({
          title: t("channel.leaderboard.channelActionsEdit.error"),
          message: `${t("channel.leaderboard.channelActionsEdit.failedToCreateAction")}: ${prettifyGraphqlError(
            response?.errors,
          )}`,
          type: "error",
        });
      } else if ("error" in data) {
        toastStore.addToast({
          title: t("channel.leaderboard.channelActionsEdit.error"),
          message: data.error,
          type: "error",
        });
      } else {
        toastStore.addToast({
          title: t("channel.leaderboard.channelActionsEdit.actionCreated"),
          message: t(
            "channel.leaderboard.channelActionsEdit.actionCreatedSuccessfully",
          ),
          type: "success",
        });
      }
    } else {
      const response = await updateMutation({
        input: {
          ...input,
          id: props.item.id!,
        },
      });

      const data = response?.data?.updateChannelAction;
      if (!data) {
        toastStore.addToast({
          title: t("channel.leaderboard.channelActionsEdit.error"),
          message: `${t("channel.leaderboard.channelActionsEdit.failedToUpdateAction")}: ${prettifyGraphqlError(
            response?.errors,
          )}`,
          type: "error",
        });
      } else if ("error" in data) {
        toastStore.addToast({
          title: t("channel.leaderboard.channelActionsEdit.error"),
          message: data.error,
          type: "error",
        });
      } else {
        toastStore.addToast({
          title: t("channel.leaderboard.channelActionsEdit.actionUpdated"),
          message: t(
            "channel.leaderboard.channelActionsEdit.actionUpdatedSuccessfully",
          ),
          type: "success",
        });
      }
    }

    emit("saved");
  },
);

const form = useForm({
  defaultValues: {
    title: props.item.title || "",
    shortTitle: props.item.short_title || "",
    notificationTitle: props.item.notification_title || "",
    appActionName: props.item.app_action_name || "",
  },
  validators: {
    onChange: schema,
  },
  onSubmit: async ({ value }) => {
    await saveChannelAction(value);
  },
});
</script>
